import React, { useState, useEffect, useMemo } from 'react';
import {
    RefreshCw, Server, Database, Cpu, Zap, Activity, BarChart3,
    Shield, MessageSquare, Eye, BrainCircuit, Layers, Calendar,
    TrendingUp, AlertCircle, ArrowUpRight, DollarSign, Clock,
} from 'lucide-react';
import ApiServices from '../../shared/ApiServices/ApiServices';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';

// Cast all Lucide icons to avoid @types/react bigint incompatibility
const IconRefresh = RefreshCw as any;
const IconServer = Server as any;
const IconDatabase = Database as any;
const IconCpu = Cpu as any;
const IconZap = Zap as any;
const IconActivity = Activity as any;
const IconBarChart = BarChart3 as any;
const IconShield = Shield as any;
const IconMessage = MessageSquare as any;
const IconEye = Eye as any;
const IconBrain = BrainCircuit as any;
const IconLayers = Layers as any;
const IconCalendar = Calendar as any;
const IconTrending = TrendingUp as any;
const IconAlert = AlertCircle as any;
const IconArrow = ArrowUpRight as any;
const IconDollar = DollarSign as any;
const IconClock = Clock as any;

// ─── Types ────────────────────────────────────────────────────────────────────

interface BillingCategory {
    type: string;
    price: number;
    unit: string;
}

interface CostData {
    timePeriod: { start: string; end: string };
    price: number;
    unit: string;
    billing: Record<string, BillingCategory>;
}

// ─── Category palette — US Medical / FinOps standard colors, no grey ─────────

interface CatCfg {
    icon: any;
    color: string;       // text/accent
    bg: string;          // icon background
    ring: string;        // card ring on highlight
    bar: string;         // gradient fill for bars
}

const CAT: Record<string, CatCfg> = {
    security: { icon: IconShield, color: '#E03C4A', bg: 'rgba(224,60,74,0.10)', ring: 'rgba(224,60,74,0.25)', bar: 'linear-gradient(90deg,#b01526,#E03C4A)' },
    compute: { icon: IconCpu, color: '#2B6CB0', bg: 'rgba(43,108,176,0.10)', ring: 'rgba(43,108,176,0.25)', bar: 'linear-gradient(90deg,#1a4f8e,#3B82F6)' },
    other: { icon: IconLayers, color: '#7C3AED', bg: 'rgba(124,58,237,0.10)', ring: 'rgba(124,58,237,0.25)', bar: 'linear-gradient(90deg,#4c1d95,#7C3AED)' },
    storage: { icon: IconServer, color: '#0891B2', bg: 'rgba(8,145,178,0.10)', ring: 'rgba(8,145,178,0.25)', bar: 'linear-gradient(90deg,#0e6b8c,#0891B2)' },
    networking: { icon: IconZap, color: '#EA7C19', bg: 'rgba(234,124,25,0.10)', ring: 'rgba(234,124,25,0.25)', bar: 'linear-gradient(90deg,#b85c0f,#F59E0B)' },
    analytics: { icon: IconBarChart, color: '#059669', bg: 'rgba(5,150,105,0.10)', ring: 'rgba(5,150,105,0.25)', bar: 'linear-gradient(90deg,#065f46,#10B981)' },
    messaging: { icon: IconMessage, color: '#B45309', bg: 'rgba(180,83,9,0.10)', ring: 'rgba(180,83,9,0.25)', bar: 'linear-gradient(90deg,#78350f,#D97706)' },
    monitoring: { icon: IconEye, color: '#15803D', bg: 'rgba(21,128,61,0.10)', ring: 'rgba(21,128,61,0.25)', bar: 'linear-gradient(90deg,#14532d,#22C55E)' },
    database: { icon: IconDatabase, color: '#BE185D', bg: 'rgba(190,24,93,0.10)', ring: 'rgba(190,24,93,0.25)', bar: 'linear-gradient(90deg,#831843,#EC4899)' },
    machine_learning: { icon: IconBrain, color: '#5B21B6', bg: 'rgba(91,33,182,0.10)', ring: 'rgba(91,33,182,0.25)', bar: 'linear-gradient(90deg,#3b0764,#8B5CF6)' },
};
const FALLBACK: CatCfg = { icon: IconActivity, color: '#0891B2', bg: 'rgba(8,145,178,0.10)', ring: 'rgba(8,145,178,0.25)', bar: 'linear-gradient(90deg,#0e6b8c,#0891B2)' };

// ─── Formatting helpers ────────────────────────────────────────────────────────

const fmt = (n: number): string => {
    if (n > 0 && n < 0.01) return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};
const fmtShort = (iso: string) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso + 'T12:00:00'));
const fmtLong = (iso: string) => new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso + 'T12:00:00'));
const days = (s: string, e: string) => { const ms = new Date(e + 'T12:00:00').getTime() - new Date(s + 'T12:00:00').getTime(); return Math.max(1, Math.round(ms / 86400000)); };
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const shortType = (t: string) => t.replace(' Services', '').replace('App Integration & ', '').replace(' & Identity', '').replace(' & Big Data', '').replace(' & Management', '').replace(' & AI', '');

// ─── Shimmer ──────────────────────────────────────────────────────────────────

const S: React.FC<{ w?: string; h?: string; r?: number }> = ({ w = '100%', h = '14px', r = 8 }) => (
    <div style={{ width: w, height: h, borderRadius: r, background: 'var(--v2-bg-surface)' }} />
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function BillingSkeleton() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* KPI row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 16, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <S w="120px" h="11px" />
                        <S w="80px" h="30px" r={6} />
                        <S w="90px" h="10px" />
                    </div>
                ))}
            </div>
            {/* Distribution bar */}
            <div style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 16, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <S w="160px" h="11px" />
                <S w="100%" h="24px" r={12} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {[100, 80, 70, 60, 50].map(w => <S key={w} w={`${w}px`} h="10px" />)}
                </div>
            </div>
            {/* Table skeleton */}
            <div style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--v2-border-subtle)' }}>
                    <S w="200px" h="11px" />
                </div>
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} style={{ padding: '16px 20px', borderBottom: '1px solid var(--v2-border-subtle)', display: 'flex', alignItems: 'center', gap: 16 }}>
                        <S w="28px" h="28px" r={8} />
                        <S w="130px" h="13px" />
                        <div style={{ flex: 1 }}><S w="100%" h="6px" r={99} /></div>
                        <S w="42px" h="13px" />
                        <S w="70px" h="16px" r={6} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiProps { label: string; value: string; sub?: string; icon: any; color: string }
function KpiCard({ label, value, sub, icon: Icon, color }: KpiProps) {
    return (
        <div style={{
            background: 'var(--v2-bg-card)',
            border: '1px solid var(--v2-border-card)',
            borderRadius: 16,
            padding: '20px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: 'var(--v2-shadow-card)',
        }}>
            {/* Colored left accent */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, borderRadius: '0 2px 2px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--v2-text-muted)' }}>{label}</span>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon style={{ width: 15, height: 15, color }} strokeWidth={2.5} />
                </div>
            </div>

            <p style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1, color, margin: 0 }} className="tabular-nums">{value}</p>

            {sub && <p style={{ fontSize: 11, color: 'var(--v2-text-muted)', margin: 0, fontWeight: 500 }}>{sub}</p>}
        </div>
    );
}

// ─── Stacked spend bar ────────────────────────────────────────────────────────

function SpendBar({ cats, total }: { cats: [string, BillingCategory][]; total: number }) {
    const [hovered, setHovered] = useState<string | null>(null);
    const segments = cats.filter(([, c]) => c.price > 0);
    return (
        <div>
            {/* Bar */}
            <div style={{ display: 'flex', height: 20, borderRadius: 10, overflow: 'hidden', gap: 2, background: 'var(--v2-bg-surface)' }}>
                {segments.map(([key, cat]) => {
                    const cfg = CAT[key] ?? FALLBACK;
                    const pct = (cat.price / total) * 100;
                    return (
                        <div
                            key={key}
                            title={`${shortType(cat.type)}: ${fmt(cat.price)} (${pct.toFixed(1)}%)`}
                            onMouseEnter={() => setHovered(key)}
                            onMouseLeave={() => setHovered(null)}
                            style={{
                                width: `${clamp(pct, 0.6, 100)}%`,
                                flexShrink: 0,
                                background: cfg.bar,
                                borderRadius: 4,
                                opacity: hovered && hovered !== key ? 0.3 : 1,
                                transform: hovered === key ? 'scaleY(1.15)' : 'scaleY(1)',
                                transition: 'opacity 0.18s, transform 0.15s',
                                cursor: 'default',
                            }}
                        />
                    );
                })}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 12 }}>
                {segments.map(([key, cat]) => {
                    const cfg = CAT[key] ?? FALLBACK;
                    const pct = ((cat.price / total) * 100).toFixed(1);
                    return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: cfg.color, display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-text-secondary)', whiteSpace: 'nowrap' }}>
                                {shortType(cat.type)}
                                <span style={{ color: 'var(--v2-text-muted)', marginLeft: 4 }}>{pct}%</span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Category table row (desktop) ─────────────────────────────────────────────

function CatRow({ rank, catKey, cat, cfg, total }: { rank: number; catKey: string; cat: BillingCategory; cfg: CatCfg; total: number }) {
    const [hov, setHov] = useState(false);
    const pct = total > 0 ? (cat.price / total) * 100 : 0;
    const Icon = cfg.icon;
    return (
        <div
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '13px 20px',
                background: hov ? `${cfg.color}07` : 'transparent',
                borderLeft: `3px solid ${hov ? cfg.color : 'transparent'}`,
                transition: 'background 0.15s, border-color 0.15s',
            }}
        >
            {/* Rank */}
            <span style={{ width: 22, textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--v2-text-muted)', flexShrink: 0 }}>#{rank}</span>

            {/* Icon */}
            <div style={{ width: 32, height: 32, borderRadius: 9, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon style={{ width: 15, height: 15, color: cfg.color }} strokeWidth={2} />
            </div>

            {/* Name */}
            <div style={{ flex: '0 0 160px', minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--v2-text-heading)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {shortType(cat.type)}
                </p>
            </div>

            {/* Bar */}
            <div style={{ flex: 1, minWidth: 60 }}>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--v2-border-subtle)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${clamp(pct, 0.3, 100)}%`, background: cfg.bar, borderRadius: 99, transition: 'width 0.5s var(--v2-ease-calm)' }} />
                </div>
            </div>

            {/* Pct pill */}
            <span style={{ flexShrink: 0, minWidth: 45, textAlign: 'center', fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '2px 8px', borderRadius: 99 }} className="tabular-nums">
                {pct.toFixed(1)}%
            </span>

            {/* Cost */}
            <span style={{ flexShrink: 0, minWidth: 80, textAlign: 'right', fontSize: 14, fontWeight: 800, color: cfg.color, letterSpacing: '-0.02em' }} className="tabular-nums">
                {fmt(cat.price)}
            </span>
        </div>
    );
}

// ─── Category card (mobile / tablet) ──────────────────────────────────────────

function CatCard({ catKey, cat, cfg, total }: { catKey: string; cat: BillingCategory; cfg: CatCfg; total: number }) {
    const pct = total > 0 ? (cat.price / total) * 100 : 0;
    const Icon = cfg.icon;
    return (
        <div style={{
            position: 'relative',
            background: 'var(--v2-bg-card)',
            border: `1px solid var(--v2-border-card)`,
            borderRadius: 16,
            padding: '18px 18px 16px',
            overflow: 'hidden',
            boxShadow: 'var(--v2-shadow-card)',
        }}>
            {/* top accent */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: cfg.bar }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon style={{ width: 18, height: 18, color: cfg.color }} strokeWidth={2} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '2px 9px', borderRadius: 99 }} className="tabular-nums">
                    {pct.toFixed(1)}%
                </span>
            </div>

            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--v2-text-muted)', marginBottom: 4, margin: '0 0 4px' }}>{shortType(cat.type)}</p>
            <p style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, color: cfg.color, margin: '0 0 12px' }} className="tabular-nums">{fmt(cat.price)}</p>

            <div style={{ height: 4, borderRadius: 99, background: 'var(--v2-border-subtle)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${clamp(pct, 0.3, 100)}%`, background: cfg.bar, borderRadius: 99 }} />
            </div>
        </div>
    );
}

// ─── Insight chip ─────────────────────────────────────────────────────────────

function Insight({ icon: Icon, title, desc, color }: { icon: any; title: string; desc: string; color: string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--v2-shadow-sm)' }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon style={{ width: 15, height: 15, color }} strokeWidth={2.5} />
            </div>
            <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-heading)', margin: '0 0 3px' }}>{title}</p>
                <p style={{ fontSize: 11.5, lineHeight: 1.55, color: 'var(--v2-text-muted)', margin: 0 }}>{desc}</p>
            </div>
        </div>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onRefresh }: { onRefresh: () => void }) {
    return (
        <div style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 20, padding: '72px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, textAlign: 'center', boxShadow: 'var(--v2-shadow-card)' }}>
            <div style={{ width: 60, height: 60, borderRadius: 16, background: 'rgba(43,108,176,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconBarChart style={{ width: 28, height: 28, color: '#2B6CB0' }} strokeWidth={2} />
            </div>
            <div>
                <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--v2-text-heading)', margin: '0 0 6px' }}>No Billing Data</p>
                <p style={{ fontSize: 13, color: 'var(--v2-text-muted)', margin: 0 }}>Unable to load AWS Cost Explorer data. Try refreshing below.</p>
            </div>
            <button
                onClick={onRefresh}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px', borderRadius: 10, background: '#2B6CB0', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
                <IconRefresh style={{ width: 14, height: 14 }} strokeWidth={2.5} />
                Retry
            </button>
        </div>
    );
}

// ─── Responsive breakpoint hook ──────────────────────────────────────────────
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
    useEffect(() => {
        const upd = () => setIsDesktop(window.innerWidth >= 1024);
        window.addEventListener('resize', upd);
        return () => window.removeEventListener('resize', upd);
    }, []);
    return isDesktop;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Billing() {
    const [loading, setLoading] = useState(false);
    const [costData, setCostData] = useState<CostData | null>(null);
    const isDesktop = useIsDesktop();

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await ApiServices.getBillingCost();
            setCostData(res ?? null);
        } catch {
            setCostData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    // Sorted highest → lowest cost
    const categories = useMemo<[string, BillingCategory][]>(
        () => costData ? Object.entries(costData.billing).sort(([, a], [, b]) => b.price - a.price) : [],
        [costData]
    );

    const total = costData?.price ?? 0;
    const dailyAvg = total > 0 && costData ? total / days(costData.timePeriod.start, costData.timePeriod.end) : 0;
    const periodDays = costData ? days(costData.timePeriod.start, costData.timePeriod.end) : 0;
    const top = categories[0];
    const topCfg = CAT[top?.[0] ?? ''] ?? FALLBACK;
    const topPct = total > 0 ? Math.round(((top?.[1]?.price ?? 0) / total) * 100) : 0;
    const compute = costData?.billing.compute?.price ?? 0;
    const storage = costData?.billing.storage?.price ?? 0;
    const infraPct = total > 0 ? Math.round(((compute + storage) / total) * 100) : 0;

    return (
        <div style={{ position: 'relative' }}>
            {/* ── PAGE HEADER ── */}
            <ModernPageHeader
                title="Billing Overview"
                subtitle={costData
                    ? `${fmtLong(costData.timePeriod.start)} – ${fmtLong(costData.timePeriod.end)}`
                    : 'AWS cloud infrastructure cost breakdown'}
                actions={
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '7px 16px', borderRadius: 9,
                            background: 'var(--v2-bg-surface)',
                            border: '1px solid var(--v2-border-card)',
                            color: 'var(--v2-text-secondary)',
                            fontSize: 12, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1,
                            transition: 'opacity 0.2s',
                        }}
                    >
                        <IconRefresh style={{ width: 13, height: 13 }} strokeWidth={2.5} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                }
            />

            {/* ── SKELETON ── */}
            {loading && <BillingSkeleton />}

            {/* ── EMPTY ── */}
            {!loading && !costData && <EmptyState onRefresh={fetchData} />}

            {/* ══════════════════════════════════════════════
                    MAIN CONTENT
            ══════════════════════════════════════════════ */}
            {!loading && costData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* ── (A) KPI HERO ROW ───────────────────────────────── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
                        <KpiCard
                            label="Total Cloud Spend"
                            value={fmt(total)}
                            sub={`${fmtShort(costData.timePeriod.start)} – ${fmtShort(costData.timePeriod.end)}`}
                            icon={IconDollar}
                            color="#2B6CB0"
                        />
                        <KpiCard
                            label="Daily Average"
                            value={fmt(dailyAvg)}
                            sub={`Over ${periodDays} billing days`}
                            icon={IconClock}
                            color="#059669"
                        />
                        <KpiCard
                            label="Top Category"
                            value={`${topPct}%`}
                            sub={shortType(top?.[1]?.type ?? '—')}
                            icon={topCfg.icon}
                            color={topCfg.color}
                        />
                        <KpiCard
                            label="Infra Share"
                            value={`${infraPct}%`}
                            sub="Compute + Storage"
                            icon={IconServer}
                            color="#0891B2"
                        />
                        <KpiCard
                            label="Categories"
                            value={String(categories.length)}
                            sub="AWS service groups"
                            icon={IconCalendar}
                            color="#7C3AED"
                        />
                    </div>

                    {/* ── (B) SPEND DISTRIBUTION BAR ────────────────────── */}
                    <div style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 16, padding: '18px 24px', boxShadow: 'var(--v2-shadow-card)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--v2-text-muted)', margin: 0 }}>Spend Distribution</p>
                            <span style={{ fontSize: 11, color: 'var(--v2-text-muted)', fontWeight: 500 }}>Hover segment to inspect</span>
                        </div>
                        <SpendBar cats={categories} total={total} />
                    </div>

                    {/* ── (C) CATEGORY BREAKDOWN ────────────────────────── */}
                    <div>
                        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--v2-text-muted)', margin: '0 0 12px' }}>
                            Service Categories — ranked by spend
                        </p>

                        {/* MOBILE CARD GRID */}
                        {!isDesktop && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                                {categories.map(([key, cat]) => (
                                    <CatCard key={key} catKey={key} cat={cat} cfg={CAT[key] ?? FALLBACK} total={total} />
                                ))}
                            </div>
                        )}

                        {/* DESKTOP TABLE */}
                        {isDesktop && (
                            <div style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--v2-shadow-card)' }}>
                                {/* Table header */}
                                <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--v2-border-subtle)', background: 'var(--v2-bg-surface)', display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <span style={{ width: 22, flexShrink: 0 }} />
                                    <span style={{ width: 32, flexShrink: 0 }} />
                                    <span style={{ flex: '0 0 160px', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--v2-text-muted)' }}>Category</span>
                                    <span style={{ flex: 1, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--v2-text-muted)' }}>Distribution</span>
                                    <span style={{ minWidth: 45, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--v2-text-muted)', textAlign: 'center' }}>Share</span>
                                    <span style={{ minWidth: 80, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--v2-text-muted)', textAlign: 'right' }}>Cost (USD)</span>
                                </div>

                                {/* Rows */}
                                {categories.map(([key, cat], idx) => (
                                    <div key={key} style={{ borderBottom: idx < categories.length - 1 ? '1px solid var(--v2-border-subtle)' : 'none' }}>
                                        <CatRow rank={idx + 1} catKey={key} cat={cat} cfg={CAT[key] ?? FALLBACK} total={total} />
                                    </div>
                                ))}

                                {/* Footer total row */}
                                <div style={{ padding: '11px 20px', borderTop: '2px solid var(--v2-border-card)', background: 'var(--v2-bg-surface)', display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <span style={{ width: 22, flexShrink: 0 }} />
                                    <span style={{ width: 32, flexShrink: 0 }} />
                                    <span style={{ flex: '0 0 160px', fontSize: 12, fontWeight: 700, color: 'var(--v2-text-secondary)' }}>Total</span>
                                    <span style={{ flex: 1 }} />
                                    <span style={{ minWidth: 45, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--v2-text-secondary)' }} className="tabular-nums">100%</span>
                                    <span style={{ minWidth: 80, textAlign: 'right', fontSize: 16, fontWeight: 900, color: '#2B6CB0', letterSpacing: '-0.025em' }} className="tabular-nums">{fmt(total)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
